/* eslint-disable max-len */
import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import styled from 'styled-components';
import {Link, useHistory} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import * as moment from 'moment';

import {getCurrentLocale} from './../../utils/i18n';
import {contentUrlSolver} from './../../helpers';

import {ReactComponent as PopcornIcon} from '../../assets/popcorn_icon.svg';
import PlayIcon from '../../assets/play_icon.png';
import {
    mobileMediaQuery,
    tabletMediaQuery,
    notWebMediaQuery,
    useIsMobile,
} from '../../styles';

const Container = styled(Link)`
    width: 100%;
    height: 330px;
    background-image: url(${({background}) => background});
    background-size: cover;
    background-position: 50% 50%;
    background-repeat: no-repeat;
    margin-bottom: 30px;
    overflow: hidden;
    position: relative;

    display: flex;

    ${tabletMediaQuery} {
        height: 296px;
    }

    ${mobileMediaQuery} {
        height: 312px;
        margin-bottom: 12px;
    }
`;

const HeroGradient = styled.div`
    position: absolute;
    bottom: 0px;
    left: 0px;
    right: 0px;
    height: 146px;
    background: linear-gradient(0deg, rgb(0, 0, 0) 0%, rgba(0, 0, 0, 0) 100%);
`;

export const InfoContainer = styled.div`
    position: absolute;
    bottom: 30px;
    left: 40px;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;

    ${mobileMediaQuery} {
        left: 0px;
        right: 0px;
        padding: 0px 25px;
        box-sizing: border-box;
    }
`;

const DurationContainer = styled.div`
    background: rgb(8, 0, 8);
    border-radius: 0px;
    height: 28px;
    width: auto;
    padding: 5px 11px 6px 9px;
    margin-bottom: 6px;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;

    svg,
    img {
        height: 17px;
        width: 13px;
        margin-right: 6px;
        object-fit: contain;
    }

    color: rgb(255, 255, 255);
    font-family: 'AvenirNextRoundedStd';
    font-size: 10px;
    font-weight: bold;
    line-height: 16px;
    text-align: center;
    text-transform: uppercase;
`;

const LiveCircle = styled.div`
    height: 8px;
    width: 8px;
    border-radius: 100%;
    background: rgb(237, 0, 128);
    margin-right: 6px;
`;

const Title = styled.h2`
    color: rgb(255, 255, 255);
    font-family: 'TTNormsProExtraBlack';
    font-size: 24px;
    line-height: 24px;
    height: auto;
    white-space: pre-line;
    text-transform: uppercase;
    max-width: 50%;
    margin: 0px;

    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;

    ${notWebMediaQuery} {
        max-width: initial;
    }

    ${mobileMediaQuery} {
        width: 100%;
        font-size: 20px;
    }
`;

const Subtitle = styled.span`
    margin-top: 10px;
    color: rgb(255, 255, 255);
    font-family: 'AvenirNextRoundedStd';
    font-size: 14px;
    font-weight: bold;

    line-height: 16px;
    text-transform: uppercase;

    ${mobileMediaQuery} {
        font-weight: 500;
        margin-top: 8px;
    }
`;

const PlayButton = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;

    margin-left: 1.5rem;
    background-color: #fff;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 45px;
    min-width: 45px;
    height: 45px;
    min-height: 45px;
    padding: 0px;
    border-radius: 100%;
    svg rect {
        fill: #000;
    }
    z-index: 1;

    cursor: pointer;

    ${mobileMediaQuery} {
        margin-left: auto;
    }
`;

const Spacer = styled.div`
    ${notWebMediaQuery} {
        height: 78px;
    }
`;

const Spotlights = ({
    pickAndPlace,
    movie_metadata,
    id,
    background,
    type,
    title,
    subtitle,
    ...props
}) => {
    const history = useHistory();
    const config = useSelector(state => state.config);
    const user = useSelector(state => state.user);
    const {t} = useTranslation();
    const isMobile = useIsMobile();

    let defaultPnp = null;

    if (movie_metadata) {
        defaultPnp = {
            id: movie_metadata.id,
            title: twoLinesTitle(movie_metadata.title),
            duration: movie_metadata.duration,
            image: contentUrlSolver(
                config,
                movie_metadata.image,
                `HERO-${isMobile ? 'M' : 'T'}`,
                'png',
            ),
            type: 'movie',
        };
    }

    const [item, setItem] = useState(undefined);

    const state = useSelector(state => state);

    useEffect(() => {
        const getPnp = async () => {
            let pnp = pickAndPlace ? await loadPNP(state, pickAndPlace) : null;

            let currentCatalog = user.jwtToken ? user.catalogTenant : null;

            if (!currentCatalog) {
                currentCatalog =
                    getCurrentLocale() === 'por'
                        ? 'CNPLUS_LATAM_BR'
                        : 'CNPLUS_LATAM_LATAM';
            }

            const currentPickAndPlace = pickAndPlace.find(pnp =>
                pnp.catalog.includes(currentCatalog),
            );

            if (
                (!movie_metadata || !id || !background || !type || !title) &&
                currentPickAndPlace
            ) {
                // eslint-disable-next-line react-hooks/exhaustive-deps
                defaultPnp = {
                    title: twoLinesTitle(
                        currentPickAndPlace.title[getCurrentLocale()],
                    ),
                    image: isMobile
                        ? currentPickAndPlace.image
                        : currentPickAndPlace.imageTablet,
                    type: currentPickAndPlace.contentType,
                };
            }

            setItem({...defaultPnp, ...(pnp || {})});
        };

        if (id && background && type && title) {
            setItem({
                id: id,
                title: title,
                subtitle: subtitle,
                image: background,
                type: type,
            });
        } else {
            getPnp();
        }

        // eslint-disable-next-line
    }, [isMobile]);

    return item ? (
        <Container background={item.image} {...props} to={props.link}>
            <HeroGradient />

            <InfoContainer>
                {
                    <DurationContainer>
                        {item.type === 'announcement' &&
                            t('pickAndPlace.comingSoon')}

                        {item.type === 'comic' && props.hint}

                        {item.type === 'live' && (
                            <>
                                <LiveCircle title="" aria-hidden="true" />
                                {item.subtitle || t('pickAndPlace.live')}
                            </>
                        )}

                        {(item.type === 'movie' ||
                            (!item.type &&
                                item.contentType === 'VIDEO' &&
                                item.duration)) && (
                            <>
                                <PopcornIcon title="" aria-hidden="true" />
                                {item.duration && item.duration > 0
                                    ? moment()
                                          .startOf('day')
                                          .seconds(item.duration)
                                          .format(
                                              item.duration / 60 > 60
                                                  ? 'H[h] mm[m]'
                                                  : 'mm[m]',
                                          )
                                    : null}
                            </>
                        )}
                    </DurationContainer>
                }

                <Title>
                    {item.title}

                    {item.type !== 'announcement' &&
                        item.type !== 'comic' &&
                        item.id && (
                            <PlayButton
                                onClick={() =>
                                    history.push(
                                        `/${
                                            item.type === 'live'
                                                ? 'now'
                                                : 'play'
                                        }/${item.id}`,
                                    )
                                }
                            >
                                <img src={PlayIcon} alt={item.title} />
                            </PlayButton>
                        )}

                    {item.type === 'comic' && (
                        <ImageContainer>
                            <Icon
                                src={require('../../assets/ic_cn_comics.png')}
                            />
                        </ImageContainer>
                    )}
                </Title>

                {item.subtitle && item.type !== 'live' && (
                    <Subtitle>{item.subtitle}</Subtitle>
                )}
            </InfoContainer>
        </Container>
    ) : (
        <Spacer />
    );
};

export const loadPNP = async (state, pnpSchedule) => {
    let {user} = state;

    let catalog = user.catalogTenant;

    const _verifyPnP = schedule => {
        if (schedule.start && schedule.end) {
            let now = new Date();
            let start = new Date(schedule.start);
            let end = new Date(schedule.end);
            //not valid if outside range
            if (now < start || now > end) {
                return false;
            }
        }
        //check if our catalog is included in this rule
        if (schedule.catalog && schedule.catalog.indexOf(catalog) === -1) {
            return false;
        }
        return true;
    };

    //check each schedule to see if it is the current valid schedule
    if (pnpSchedule.length === 0) {
        return;
    } else {
        let schedule = pnpSchedule.find(_verifyPnP);

        if (!schedule) {
            return;
        }
        //check if this content needs loading
        let locale = getCurrentLocale();
        if (schedule && schedule.contentType === 'announcement') {
            let content = pnpToContent(schedule, locale);
            if (content) {
                return content;
            } else {
                return null;
            }
        } else {
            return null;
        }
    }
};

const twoLinesTitle = title => {
    let titleWords = title.split(' ');
    let midSpace = Math.round(title.split(' ').length / 2);
    titleWords.splice(midSpace, 0, '\n');
    let newTitle =
        title.length * 16 > window.innerWidth * 0.7
            ? titleWords.join(' ')
            : title;
    return newTitle;
};

const pnpToContent = (schedule, locale) => {
    let item = {};
    //basic info must be present
    item.title = schedule.title ? schedule.title[locale] : null;
    item.subtitle = schedule.subtitle ? schedule.subtitle[locale] : null;
    item.image = schedule.imageTablet;
    item.imageMobile = schedule.image;
    item.id = undefined;
    item.duration = undefined;
    item.type = 'announcement';

    if (!item.title || !item.image) {
        return {};
    }
    return item;
};

const ImageContainer = styled.div`
    position: relative;
    width: 57px;
    height: 20px;
`;

const Icon = styled.img`
    position: absolute;
    width: 37px;
    height: 32px;
    margin-top: -4px;
    margin-left: 20px;
`;

export default Spotlights;
